import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { toast } from 'sonner'
import { queryClient, trpc } from '@/lib/trpc'
import { vendorLogoPath } from '@/lib/vendor-logo'
import { openOAuthConnectPopup } from '../oauth-connect-lifecycle'
import type { ConfigModalProps, IntegrationProvider } from '../types'

function ClickUpConfigModal({
  open,
  onOpenChange,
  integration,
  workspaceId,
}: ConfigModalProps) {
  const integrationId = integration?.id ?? ''
  const [clickupWorkspaceId, setClickupWorkspaceId] = useState('')
  const [listIds, setListIds] = useState<string[]>([])
  const [offset, setOffset] = useState(0)
  const [accessOffset, setAccessOffset] = useState(0)
  const [connecting, setConnecting] = useState(false)
  const scope = useQuery({
    ...trpc.integrations.clickup.getScope.queryOptions({ integrationId }),
    enabled: open && !!integrationId,
    refetchInterval: open && integrationId ? 3000 : false,
  })
  const workspaces = useQuery({
    ...trpc.integrations.clickup.workspaces.queryOptions({ integrationId }),
    enabled: open && !!integrationId,
  })
  const lists = useQuery({
    ...trpc.integrations.clickup.lists.queryOptions({
      integrationId,
      clickupWorkspaceId,
    }),
    enabled: open && !!integrationId && !!clickupWorkspaceId,
  })
  const tasks = useQuery({
    ...trpc.integrations.clickup.tasks.queryOptions({ integrationId, offset }),
    enabled: open && !!integrationId,
  })
  const access = useQuery({
    ...trpc.integrations.clickup.access.queryOptions({
      integrationId,
      offset: accessOffset,
    }),
    enabled: open && !!integrationId,
  })
  const save = useMutation(
    trpc.integrations.clickup.saveScope.mutationOptions()
  )
  const sync = useMutation(trpc.integrations.clickup.sync.mutationOptions())
  const busy =
    !!scope.data?.syncState &&
    !['completed', 'failed'].includes(scope.data.syncState)

  const savedScope = scope.data?.scope
  useEffect(() => {
    if (savedScope !== undefined) {
      setClickupWorkspaceId(savedScope?.workspaceId ?? '')
      setListIds(savedScope?.lists.map((list) => list.id) ?? [])
    }
  }, [savedScope, integrationId])
  useEffect(() => {
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.clickup.tasks.queryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: trpc.integrations.clickup.access.queryKey(),
    })
  }, [scope.data?.lastSyncedAt])

  const error =
    scope.error ||
    workspaces.error ||
    lists.error ||
    tasks.error ||
    access.error
  const saveAndSync = async () => {
    try {
      await save.mutateAsync({ integrationId, clickupWorkspaceId, listIds })
      await sync.mutateAsync({ integrationId })
      setOffset(0)
      await scope.refetch()
      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.clickup.tasks.queryKey(),
      })
      await queryClient.invalidateQueries({
        queryKey: trpc.integrations.clickup.access.queryKey(),
      })
      setAccessOffset(0)
      toast.success('ClickUp collection queued')
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : 'Could not start ClickUp collection'
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] overflow-y-auto sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>ClickUp tasks, comments and access</DialogTitle>
          <DialogDescription>
            Collect workspace members and roles, plus tasks and comments from
            selected lists. Screenata reads data without changing ClickUp.
          </DialogDescription>
        </DialogHeader>
        {!integrationId ? (
          <Button
            disabled={connecting}
            onClick={async () => {
              setConnecting(true)
              try {
                await ClickUpProvider.handleConnect({
                  workspaceId,
                  onFinish: () => setConnecting(false),
                })
              } catch (err) {
                setConnecting(false)
                toast.error(
                  err instanceof Error
                    ? err.message
                    : 'Could not connect to ClickUp'
                )
              }
            }}
          >
            {connecting ? 'Connecting…' : 'Connect ClickUp'}
          </Button>
        ) : (
          <div className='space-y-5'>
            {error ? (
              <p role='alert' className='text-destructive text-sm'>
                {error.message}
              </p>
            ) : null}
            <div className='space-y-2'>
              <label
                htmlFor='clickup-workspace'
                className='text-sm font-medium'
              >
                ClickUp workspace
              </label>
              <Select
                value={clickupWorkspaceId}
                disabled={
                  busy ||
                  save.isPending ||
                  sync.isPending ||
                  workspaces.isLoading
                }
                onValueChange={(value) => {
                  setClickupWorkspaceId(value)
                  setListIds([])
                }}
              >
                <SelectTrigger id='clickup-workspace'>
                  <SelectValue
                    placeholder={
                      workspaces.isLoading
                        ? 'Loading workspaces…'
                        : 'Select a workspace'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.data?.map((workspace) => (
                    <SelectItem key={workspace.id} value={workspace.id}>
                      {workspace.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {workspaces.data?.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                  No accessible ClickUp workspaces.
                </p>
              ) : null}
            </div>
            {clickupWorkspaceId ? (
              <fieldset
                className='space-y-2'
                disabled={busy || save.isPending || sync.isPending}
              >
                <legend className='mb-2 text-sm font-medium'>Lists</legend>
                {lists.isLoading ? (
                  <p className='text-muted-foreground text-sm'>
                    Loading lists…
                  </p>
                ) : null}
                {lists.data?.length === 0 ? (
                  <p className='text-muted-foreground text-sm'>
                    No accessible lists in this workspace.
                  </p>
                ) : null}
                <div className='max-h-48 space-y-2 overflow-y-auto'>
                  {lists.data?.map((list) => (
                    <label
                      key={list.id}
                      className='flex items-center gap-2 text-sm'
                    >
                      <Checkbox
                        checked={listIds.includes(list.id)}
                        onCheckedChange={(checked) =>
                          setListIds((current) =>
                            checked === true
                              ? [...current, list.id]
                              : current.filter((gid) => gid !== list.id)
                          )
                        }
                      />
                      {list.space.name ?? list.space.id} / {list.name}
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
            <div className='flex flex-wrap items-center gap-3'>
              <Button
                disabled={
                  busy ||
                  save.isPending ||
                  sync.isPending ||
                  !listIds.length ||
                  lists.isLoading ||
                  !!lists.error
                }
                onClick={() => void saveAndSync()}
              >
                {busy
                  ? 'Collecting…'
                  : save.isPending || sync.isPending
                    ? 'Starting…'
                    : 'Save lists and collect'}
              </Button>
              <p role='status' className='text-muted-foreground text-sm'>
                {busy
                  ? 'Collection runs in the background. You can close this dialog.'
                  : scope.data?.lastSyncedAt
                    ? `Last collected ${new Date(scope.data.lastSyncedAt).toLocaleString()}`
                    : 'No completed collection for this selection.'}
              </p>
            </div>
            {scope.data?.lastError ? (
              <p role='alert' className='text-destructive text-sm'>
                Collection failed: {scope.data.lastError} Previously collected
                data is retained.
              </p>
            ) : null}
            <section className='space-y-3 border-t pt-4'>
              <h3 className='text-sm font-medium'>
                Collected workspace access
              </h3>
              <p className='text-muted-foreground text-sm'>
                Workspace members and observed role assignments. MFA, SSO
                enforcement and account lifecycle are not verified; custom roles
                may require additional permissions. No employees are created or
                deactivated.
              </p>
              {scope.data?.accessWarning ? (
                <p role='status' className='text-muted-foreground text-sm'>
                  {scope.data.accessWarning}
                </p>
              ) : null}
              {access.isLoading ? (
                <p className='text-sm'>Loading access…</p>
              ) : null}
              {access.data?.records.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                  No collected access records for the saved workspace.
                </p>
              ) : null}
              {access.data?.records.map((record) => (
                <div key={record.id} className='rounded-md border p-3 text-sm'>
                  <p className='font-medium'>{record.name}</p>
                  <p>{record.detail}</p>
                  <p className='text-muted-foreground text-xs'>
                    Collected {new Date(record.collectedAt).toLocaleString()}
                  </p>
                </div>
              ))}
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  disabled={!accessOffset || access.isFetching}
                  onClick={() =>
                    setAccessOffset((value) => Math.max(0, value - 25))
                  }
                >
                  Previous access
                </Button>
                <Button
                  variant='outline'
                  disabled={!access.data?.hasMore || access.isFetching}
                  onClick={() => setAccessOffset((value) => value + 25)}
                >
                  Next access
                </Button>
              </div>
            </section>
            <section className='space-y-3 border-t pt-4'>
              <h3 className='text-sm font-medium'>Collected tasks</h3>
              <p className='text-muted-foreground text-sm'>
                Includes open/closed tasks and subtasks in selected active
                lists. Comments exclude threaded replies and are not a full
                change history or proof of approval.
              </p>
              {tasks.isLoading ? (
                <p className='text-sm'>Loading tasks…</p>
              ) : null}
              {tasks.data?.tasks.length === 0 ? (
                <p className='text-muted-foreground text-sm'>
                  No collected tasks for the saved list selection.
                </p>
              ) : null}
              {tasks.data?.tasks.map((task) => (
                <details key={task.id} className='rounded-md border p-3'>
                  <summary className='cursor-pointer text-sm font-medium'>
                    {task.name} · {task.status.status} ·{' '}
                    {task.assignees
                      .map((user) => user.username ?? user.id)
                      .join(', ') || 'Unassigned'}
                  </summary>
                  <div className='mt-3 space-y-3 text-sm'>
                    <a
                      className='text-primary underline'
                      href={`https://app.clickup.com/t/${encodeURIComponent(task.id)}`}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Open in ClickUp
                    </a>
                    <p className='text-muted-foreground text-xs'>
                      Collected {new Date(task.collectedAt).toLocaleString()}.
                      Retained snapshots may include tasks later moved or
                      deleted in ClickUp.
                    </p>
                    <p className='whitespace-pre-wrap'>{task.description}</p>
                    <h4 className='font-medium'>Available comments</h4>
                    {!task.comments.length ? (
                      <p className='text-muted-foreground'>
                        ClickUp returned no comments. This does not establish
                        approval.
                      </p>
                    ) : (
                      <ol className='space-y-3'>
                        {task.comments.map((story) => (
                          <li key={story.id}>
                            <p className='text-muted-foreground text-xs'>
                              {new Date(Number(story.date)).toLocaleString()} ·{' '}
                              {story.user?.username ?? 'Unknown author'}
                            </p>
                            <p className='whitespace-pre-wrap'>
                              {story.comment_text ||
                                'No text returned for this comment.'}
                            </p>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </details>
              ))}
              <div className='flex gap-2'>
                <Button
                  variant='outline'
                  disabled={!offset || tasks.isFetching}
                  onClick={() =>
                    setOffset((current) => Math.max(0, current - 25))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant='outline'
                  disabled={!tasks.data?.hasMore || tasks.isFetching}
                  onClick={() => setOffset((current) => current + 25)}
                >
                  Next
                </Button>
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export const ClickUpProvider: IntegrationProvider = {
  id: 'ClickUp',
  name: 'ClickUp',
  description: 'Read workspace access and selected list tasks/comments',
  Icon: () => <img src={vendorLogoPath('clickup')} alt='' className='size-6' />,
  ConfigModal: ClickUpConfigModal,
  postAuthAction: 'CONFIGURE',
  getFeatures: () => [
    {
      id: 'tasks',
      name: 'Tasks, comments and access',
      description: 'Read-only task collection',
      enabled: true,
    },
  ],
  handleConnect: async ({ onSuccess, onFinish }) => {
    const result = await queryClient
      .getMutationCache()
      .build(queryClient, trpc.integrations.clickup.initAuth.mutationOptions())
      .execute(undefined)
      .catch((error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : 'Could not start ClickUp authorization'
        )
        onFinish?.('error')
        throw error
      })
    openOAuthConnectPopup({
      authUrl: result.authUrl,
      windowName: 'clickup-auth',
      provider: 'ClickUp',
      onSuccess,
      onFinish,
      onError: (message) => toast.error(message),
      onPopupBlocked: () => toast.error('Allow popups to connect ClickUp.'),
    })
  },
  onConnectComplete: async ({ code, state }) => {
    const result = await queryClient
      .getMutationCache()
      .build(
        queryClient,
        trpc.integrations.clickup.completeAuth.mutationOptions()
      )
      .execute({ code, state })
    await Promise.all(
      [
        trpc.integrations.getWorkspaceIntegrations.queryKey(),
        trpc.integrations.clickup.getScope.queryKey(),
        trpc.integrations.clickup.workspaces.queryKey(),
        trpc.integrations.clickup.lists.queryKey(),
        trpc.integrations.clickup.tasks.queryKey(),
        trpc.integrations.clickup.access.queryKey(),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey }))
    )
    return result
  },
  syncData: async (integration) => {
    await queryClient
      .getMutationCache()
      .build(queryClient, trpc.integrations.clickup.sync.mutationOptions())
      .execute({ integrationId: integration.id })
    await queryClient.invalidateQueries({
      queryKey: trpc.integrations.clickup.getScope.queryKey(),
    })
  },
}
